// --------------------------------------------
// Main JavaScript - DeSight Shopify Theme
// --------------------------------------------
// Einstiegspunkt für alle JavaScript-Module
// Diese Datei wird von Gulp kompiliert zu /assets/main.min.js
// --------------------------------------------

import { CustomCart } from './modules/cart.js';
import { CustomProduct } from './modules/product.js';
import { CustomNavigation } from './modules/navigation.js';
import { LogoBanner } from './modules/logo-banner.js';
import { HeaderBanner } from './modules/header-banner.js';
import { ProduktSpotlightSlider, initProduktSpotlightSliders } from './modules/produkt-spotlight.js';
import { MediaWithTextSlider, initMediaWithTextSliders } from './modules/media-with-text.js';
import { CollectionSort, initCollectionSort } from './modules/collection-sort.js';
import { initQuickFilters } from './modules/quick-filters.js';
import { initEigenschaftenSliders } from './modules/eigenschaften.js';
import { initFAQs } from './modules/faqs.js';
import { initSmoothScroll } from './modules/smooth-scroll.js';
import { initBoldText } from './modules/bold-text.js';
import { initFacetsZindex } from './modules/facets-zindex.js';
import { initMobileFacets } from './modules/mobile-facets.js';
import { initLazyLoad } from './modules/lazy-load.js';
import { initCustomLogo } from './modules/custom-logo.js';
import { initContactForms, initFloatingContact, initCustomSelects } from './modules/contact-form.js';
import { SearchPopup } from './modules/search-popup.js';
import { initStorePicker } from './modules/store-picker.js';
import { initHerstellerangaben } from './modules/herstellerangaben.js';
import { initPrintStore } from './modules/dss-print-store.js';
import { initPrintDatasheet } from './modules/dss-print-datasheet.js';
import { $ } from './utils/helpers.js';

const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

function setExternalLinksTarget() {
  const host = location.hostname;
  document.querySelectorAll('a[href^="http"]').forEach(a => {
    try {
      const url = new URL(a.href);
      if (url.hostname !== host) {
        a.target = '_blank';
        a.rel = a.rel ? `${a.rel} noopener` : 'noopener';
      }
    } catch (e) {}
  });
}

function stripProductCardSVGs() {
  if (!isTouchDevice) return;
  if (!document.body.classList.contains('template-collection')) return;

  document.querySelectorAll('.dss-product-card svg').forEach(svg => {
    svg.remove();
  });
}

function applyMobileProductCap() {
  if (!isTouchDevice) return;
  if (!document.body.classList.contains('template-collection')) return;

  const items = document.querySelectorAll('#product-grid .grid__item');
  const BATCH_SIZE = 12;
  if (items.length <= BATCH_SIZE) return;

  let visibleCount = BATCH_SIZE;

  for (let i = visibleCount; i < items.length; i++) {
    items[i].style.display = 'none';
  }

  const grid = document.getElementById('product-grid');
  if (!grid) return;

  const loadMore = document.createElement('button');
  loadMore.className = 'button button--secondary';
  loadMore.style.cssText = 'display:block;margin:20px auto;width:auto;';

  function updateButton() {
    const remaining = items.length - visibleCount;
    if (remaining <= 0) {
      loadMore.remove();
      return;
    }
    const toShow = Math.min(BATCH_SIZE, remaining);
    loadMore.textContent = `Weitere ${toShow} von ${remaining} Produkten laden`;
  }

  updateButton();

  loadMore.addEventListener('click', () => {
    const end = Math.min(visibleCount + BATCH_SIZE, items.length);
    for (let i = visibleCount; i < end; i++) {
      items[i].style.display = '';
    }
    stripProductCardSVGs();
    visibleCount = end;
    updateButton();
  });

  grid.parentNode.insertBefore(loadMore, grid.nextSibling);
}

document.addEventListener('DOMContentLoaded', () => {
  setExternalLinksTarget();

  // Disable Dawn scroll-trigger animations on touch devices to prevent
  // iOS Safari crashes ("A problem repeatedly occurred") when many product
  // cards with scroll-trigger classes become visible simultaneously.
  // scroll-trigger--cancel is Dawn's built-in mechanism (base.css:3298).
  if (isTouchDevice) {
    document.querySelectorAll('.scroll-trigger').forEach(el => {
      el.classList.add('scroll-trigger--cancel');
    });
  }

  stripProductCardSVGs();
  applyMobileProductCap();

  if ($('[data-cart-count]')) new CustomCart();
  if (document.body.classList.contains('template-product')) new CustomProduct();

  new CustomNavigation();
  LogoBanner.initAll?.();
  HeaderBanner.initAll?.();

  if ($('.produkt-spotlight__slider')) initProduktSpotlightSliders();
  if ($('[data-mwt-slider-id]')) initMediaWithTextSliders();

  // CollectionSort: Only on desktop – native <select> is better UX on touch devices
  if (!isTouchDevice && $('.facet-filters__sort, .mobile-facets__sort-select')) initCollectionSort();

  if ($('.logo-banner')) LogoBanner.initAll();
  if ($('.header-banner')) HeaderBanner.initAll();

  initCustomLogo();
  initMobileFacets();
  initFacetsZindex();

  // QuickFilters: Desktop only – touch users use the native facets drawer
  if (!isTouchDevice) initQuickFilters();

  initFAQs();
  initEigenschaftenSliders();
  initSmoothScroll();
  initBoldText();
  initLazyLoad();
  initContactForms();
  initFloatingContact();
  initCustomSelects();

  new SearchPopup();

  // [DSS] Store Picker – cookie + URL param driven store selector.
  // Renders trigger labels, manages the modal and tags Dawn's
  // pickup-availability rows for the active location.
  if ($('[data-store-picker-root]')) {
    initStorePicker();
  } else if (window.console && console.info) {
    console.info(
      '%c[DSS Store Picker]%c not initialized – no %c[data-store-picker-root]%c in DOM. ' +
        'Wahrscheinlich ist die Section noch nicht im Editor gespeichert worden, ' +
        'oder die "Store Picker aktivieren"-Checkbox ist aus. ' +
        'Theme Editor → Header → [DSS] Store Picker Einstellungen',
      'color:#7ab929;font-weight:bold',
      'color:inherit',
      'color:#d32f2f;font-weight:bold',
      'color:inherit'
    );
  }

  // [DSS] Herstellerangaben – renders richtext metaobject values
  // client-side. The snippet emits the raw portable-text JSON in
  // a <script> tag; this walks and injects the HTML.
  if ($('.dss-herstellerangaben__data')) {
    initHerstellerangaben();
  }

  // [DSS] Print – populates the "selected store" block and
  // Eigenschaften bridge in the print exposé.
  if ($('[data-dss-print-store-locations]') || $('[data-dss-eigenschaften-for-print]')) {
    initPrintStore();
  }

  // [DSS] Print Datasheet – hidden menu (7x SKU), barcode, image toggle
  initPrintDatasheet();
});
